/**
 * Baby Care Service
 *
 * Provides CRUD operations for baby care entries (feeding, diaper, sleep, etc.)
 * with support for partner sync and feature limit checks.
 */

import { supabase } from '@/lib/supabase';

// Types
export type CareType = 'feeding' | 'diaper' | 'sleep' | 'medication' | 'temperature' | 'notes';
export type FeedingType = 'breast' | 'bottle' | 'solid';
export type FeedingSide = 'left' | 'right' | 'both';
export type DiaperType = 'wet' | 'dirty' | 'both' | 'dry';
export type SleepQuality = 'good' | 'okay' | 'poor';
export type TemperatureLocation = 'ear' | 'forehead' | 'armpit' | 'rectal';

export interface BabyCareEntry {
  id: string;
  user_id: string;
  pregnancy_id: string;
  care_type: CareType;
  recorded_at: string;
  ended_at: string | null;

  // Multi-child support: 0 = first baby, 1 = second, etc.
  baby_index: number;

  // Feeding
  feeding_type: FeedingType | null;
  feeding_side: FeedingSide | null;
  feeding_amount_ml: number | null;
  feeding_duration_minutes: number | null;

  // Diaper
  diaper_type: DiaperType | null;

  // Sleep
  sleep_quality: SleepQuality | null;

  // Medication
  medication_name: string | null;
  medication_dose: string | null;

  // Temperature
  temperature_value: number | null;
  temperature_location: TemperatureLocation | null;

  // Notes
  notes: string | null;

  created_at: string;
  updated_at: string;
}

export interface CreateBabyCareEntry {
  pregnancy_id: string;
  care_type: CareType;
  baby_index?: number;
  recorded_at?: string;
  ended_at?: string | null;

  // Feeding
  feeding_type?: FeedingType;
  feeding_side?: FeedingSide;
  feeding_amount_ml?: number;
  feeding_duration_minutes?: number;

  // Diaper
  diaper_type?: DiaperType;

  // Sleep
  sleep_quality?: SleepQuality;

  // Medication
  medication_name?: string;
  medication_dose?: string;

  // Temperature
  temperature_value?: number;
  temperature_location?: TemperatureLocation;

  // Notes
  notes?: string;
}

export interface BabyCareStats {
  care_type: CareType;
  count: number;
  latest_entry: string | null;
}

export interface BabyCareDailySummary {
  care_type: CareType;
  entry_count: number;
  first_entry: string | null;
  last_entry: string | null;
  total_duration_minutes: number;
}

/**
 * Create a new baby care entry
 */
export async function createBabyCareEntry(
  userId: string,
  entry: CreateBabyCareEntry
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .insert({
      user_id: userId,
      pregnancy_id: entry.pregnancy_id,
      care_type: entry.care_type,
      baby_index: entry.baby_index ?? 0,
      recorded_at: entry.recorded_at || new Date().toISOString(),
      ended_at: entry.ended_at || null,
      feeding_type: entry.feeding_type || null,
      feeding_side: entry.feeding_side || null,
      feeding_amount_ml: entry.feeding_amount_ml || null,
      feeding_duration_minutes: entry.feeding_duration_minutes || null,
      diaper_type: entry.diaper_type || null,
      sleep_quality: entry.sleep_quality || null,
      medication_name: entry.medication_name || null,
      medication_dose: entry.medication_dose || null,
      temperature_value: entry.temperature_value || null,
      temperature_location: entry.temperature_location || null,
      notes: entry.notes || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating baby care entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Get baby care entries for a pregnancy
 */
export async function getBabyCareEntries(
  pregnancyId: string,
  options?: {
    careType?: CareType;
    babyIndex?: number;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{ data: BabyCareEntry[]; error: string | null }> {
  let query = supabase
    .from('baby_care_entries')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .order('recorded_at', { ascending: false });

  if (options?.careType) {
    query = query.eq('care_type', options.careType);
  }

  if (options?.babyIndex !== undefined) {
    query = query.eq('baby_index', options.babyIndex);
  }

  if (options?.startDate) {
    query = query.gte('recorded_at', options.startDate);
  }

  if (options?.endDate) {
    query = query.lte('recorded_at', options.endDate);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching baby care entries:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get a single baby care entry by ID
 */
export async function getBabyCareEntry(
  entryId: string
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .select('*')
    .eq('id', entryId)
    .single();

  if (error) {
    console.error('Error fetching baby care entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Update a baby care entry
 */
export async function updateBabyCareEntry(
  entryId: string,
  updates: Partial<CreateBabyCareEntry>
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    console.error('Error updating baby care entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Delete a baby care entry
 */
export async function deleteBabyCareEntry(
  entryId: string
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('baby_care_entries')
    .delete()
    .eq('id', entryId);

  if (error) {
    console.error('Error deleting baby care entry:', error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

/**
 * End a sleep or feeding session
 * @param entryId - The ID of the entry to end
 * @param endedAt - Optional custom end timestamp (defaults to now)
 */
export async function endCareSession(
  entryId: string,
  endedAt?: string
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .update({
      ended_at: endedAt || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', entryId)
    .select()
    .single();

  if (error) {
    console.error('Error ending care session:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Get baby care statistics for a pregnancy
 */
export async function getBabyCareStats(
  pregnancyId: string,
  hoursBack: number = 24,
  babyIndex?: number
): Promise<{ data: BabyCareStats[]; error: string | null }> {
  const { data, error } = await supabase
    .rpc('get_baby_care_stats', {
      p_pregnancy_id: pregnancyId,
      p_hours_back: hoursBack,
      p_baby_index: babyIndex ?? null,
    });

  if (error) {
    console.error('Error fetching baby care stats:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get daily summary for baby care
 */
export async function getBabyCareDailySummary(
  pregnancyId: string,
  date?: string,
  babyIndex?: number
): Promise<{ data: BabyCareDailySummary[]; error: string | null }> {
  const { data, error } = await supabase
    .rpc('get_baby_care_daily_summary', {
      p_pregnancy_id: pregnancyId,
      p_date: date || new Date().toISOString().split('T')[0],
      p_baby_index: babyIndex ?? null,
    });

  if (error) {
    console.error('Error fetching baby care daily summary:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get the most recent entry of a specific care type
 */
export async function getLastCareEntry(
  pregnancyId: string,
  careType: CareType
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .eq('care_type', careType)
    .order('recorded_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching last care entry:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Get all active sleep sessions (started but not ended).
 * Returns one session per baby (identified by baby_index).
 */
export async function getActiveSleepSessions(
  pregnancyId: string
): Promise<{ data: BabyCareEntry[]; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .eq('care_type', 'sleep')
    .is('ended_at', null)
    .order('recorded_at', { ascending: false });

  if (error) {
    console.error('Error fetching active sleep sessions:', error);
    return { data: [], error: error.message };
  }

  return { data: data || [], error: null };
}

/**
 * Get the most recently ended sleep session across all babies of a pregnancy.
 * Returns null if no sleep has ever been ended. Used to drive the
 * "time since last wake-up" label on the sleep quick-action button.
 */
export async function getLastEndedSleep(
  pregnancyId: string
): Promise<{ data: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('baby_care_entries')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .eq('care_type', 'sleep')
    .not('ended_at', 'is', null)
    .order('ended_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching last ended sleep:', error);
    return { data: null, error: error.message };
  }

  return { data, error: null };
}

/**
 * Get time since last entry of a specific care type
 */
export async function getTimeSinceLastCare(
  pregnancyId: string,
  careType: CareType
): Promise<{ minutes: number | null; lastEntry: BabyCareEntry | null; error: string | null }> {
  const { data, error } = await getLastCareEntry(pregnancyId, careType);

  if (error) {
    return { minutes: null, lastEntry: null, error };
  }

  if (!data) {
    return { minutes: null, lastEntry: null, error: null };
  }

  const lastTime = new Date(data.recorded_at);
  const now = new Date();
  const minutes = Math.floor((now.getTime() - lastTime.getTime()) / 1000 / 60);

  return { minutes, lastEntry: data, error: null };
}
